
import React from 'react';
import { cn } from '@/lib/utils';
import BulletinInteractionWrapper from './BulletinInteractionWrapper';
import { BulletinGlareEffect } from './BulletinGlareEffect';
import { useIsMobile } from '@/hooks/use-mobile';

interface BulletinWrapperProps {
  isSelected: boolean;
  onImageClick: (e: React.MouseEvent) => void;
  onHoverStart: (() => void) | undefined;
  onHoverEnd: (() => void) | undefined;
  onTouchStart?: () => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  children: React.ReactNode;
  'aria-label'?: string;
  role?: string;
  isAdmin?: boolean;
  isPremium?: boolean;
}

const BulletinWrapper = ({
  isSelected,
  onImageClick,
  onHoverStart,
  onHoverEnd,
  onTouchStart,
  onTouchEnd,
  children,
  'aria-label': ariaLabel,
  role,
  isAdmin,
  isPremium
}: BulletinWrapperProps) => {
  const shouldUseGlareEffect = !isSelected && (isAdmin || isPremium);
  const isMobile = useIsMobile();

  console.log(`[BulletinWrapper] Render - isSelected: ${isSelected}, isMobile: ${isMobile}`);

  // Define simpler variants for mobile devices
  const mobileVariants = {
    expanded: { 
      scale: 1,
      opacity: 1,
      transition: { duration: 0.2 }
    },
    collapsed: { 
      scale: 1,
      opacity: 1,
      transition: { duration: 0.2 }
    }
  };

  // Define more elaborate variants for desktop
  const desktopVariants = {
    expanded: { 
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3, ease: "easeInOut" }
    },
    collapsed: { 
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3, ease: "easeInOut" }
    }
  };

  const content = (
    <div
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={cn(
        "relative w-full h-full rounded-xl overflow-hidden",
        "transition-all duration-300 ease-out group",
        !isSelected && "hover:z-10 hover:scale-105"
      )}
      aria-label={ariaLabel}
      role={role}
      aria-modal={isSelected}
      aria-expanded={isSelected}
      tabIndex={isSelected ? 0 : -1}
    >
      <div 
        className={cn(
          "relative w-full h-full rounded-xl overflow-hidden"
        )}
      >
        <BulletinInteractionWrapper onImageClick={onImageClick}>
          {children}
        </BulletinInteractionWrapper>
      </div>
    </div>
  );

  // Only apply the glare effect if needed and not on mobile
  if (shouldUseGlareEffect && !isMobile) {
    return (
      <BulletinGlareEffect className="pointer-events-auto h-full w-full">
        {content}
      </BulletinGlareEffect>
    );
  }

  return content;
};

export default BulletinWrapper;
